/**
 * <savings-calculator> — E6000 savings calculator popup (model V1).
 *
 * Markup: snippets/product-savings-calculator.liquid
 * Data:   assets/savings-calculator-data.js (window.SamduoSavingsCalculatorData)
 * Time:   assets/zoned-time.js (window.SamduoZonedTime)
 *
 * Model (PRD §7):
 *   PV        = kWp × pv_yield(region)
 *   Load      = user value | annual_load(country, household)
 *   ratio     = PV / Load → band → direct_use_factor, battery_utilization_factor(N)
 *   Direct_PV = min(PV, Load) × direct_use_factor
 *   Surplus_0 = max(PV − Direct_PV, 0)        Deficit_0 = max(Load − Direct_PV, 0)
 *   Battery_Input_N  = min(Surplus_0, Deficit_0 / RTE, N × 6 × 0.95 × utilization_N)
 *   Battery_Output_N = Battery_Input_N × RTE
 *   Annual_Saving_N  = max(Output_N × import_price − Input_N × export_price, 0)
 *   Simple_Payback_N = (N × unit_price − campaign_discount_N) / Annual_Saving_N
 *
 * campaign_discount_N only applies while the campaign banner is live; tiers
 * come from `data-campaign-discounts` and the last tier caps larger bundles.
 *
 * Recommendation (PRD §8): smallest configuration whose marginal payback is
 * still reasonable (≤ 10 years); the "higher savings" priority picks the
 * configuration with the highest yearly saving instead.
 *
 * GA4 (docs/adr/0010-ga4-calculator-funnel.md): gtag('event') on page, open,
 * Check Your Results, and Buy now. That is the official custom-event setup.
 *
 * Save results (docs/adr/0011-calculator-save-results-klaviyo.md): the email
 * card posts a "Saved Calculator Results" event to the Klaviyo client API; a
 * Klaviyo flow on that metric renders and sends the results email.
 */
(() => {
  if (customElements.get('savings-calculator')) return;

  const LIMITS = {
    panels: { min: 4, max: 40 },
    load: { min: 500, max: 20000, step: 100 }
  };

  const INTL_LOCALES = { nl: 'nl-NL', de: 'de-DE', fr: 'fr-FR', en: 'en-GB' };

  const KLAVIYO = {
    fallbackCompanyId: 'WMSE7Z',
    apiBase: 'https://a.klaviyo.com/client',
    revision: '2026-07-15',
    metric: 'Saved Calculator Results'
  };
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function klaviyoCompanyId() {
    for (const script of document.querySelectorAll('script[src*="klaviyo"]')) {
      const match = script.src.match(/company_id=([A-Za-z0-9]+)/);
      if (match) return match[1];
    }
    return KLAVIYO.fallbackCompanyId;
  }

  async function postToKlaviyo(path, payload) {
    const response = await fetch(`${KLAVIYO.apiBase}/${path}/?company_id=${encodeURIComponent(klaviyoCompanyId())}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: KLAVIYO.revision
      },
      body: JSON.stringify(payload)
    });
    if (response.status !== 202) {
      throw new Error(`Klaviyo ${path} responded with ${response.status}: ${await response.text()}`);
    }
  }

  const absoluteUrl = (value) => {
    if (!value) return '';
    try {
      return new URL(value, window.location.href).href;
    } catch {
      return '';
    }
  };

  let zonedTimeReady;
  const whenZonedTimeReady = (src) => {
    if (typeof window.SamduoZonedTime?.stamp === 'function') return Promise.resolve();
    if (!zonedTimeReady) {
      zonedTimeReady = new Promise((resolve, reject) => {
        if (!src) {
          reject(new Error('[savings-calculator] zoned-time.js src missing'));
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => {
          if (typeof window.SamduoZonedTime?.stamp === 'function') resolve();
          else reject(new Error('[savings-calculator] zoned-time.js loaded without SamduoZonedTime'));
        };
        script.onerror = () => reject(new Error('[savings-calculator] zoned-time.js failed to load'));
        document.head.appendChild(script);
      }).catch((error) => {
        zonedTimeReady = null;
        throw error;
      });
    }
    return zonedTimeReady;
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const roundTo = (value, step) => Math.round(value / step) * step;
  const parseNumber = (raw) => {
    const cleaned = String(raw ?? '').replace(/\s/g, '').replace(/[^\d.,-]/g, '');
    if (!cleaned) return NaN;
    // "2.500" / "2,500" (thousands) vs "5,5" / "5.5" (decimal): treat the last
    // separator as decimal only when it is followed by 1–2 digits.
    const normalized = /[.,]\d{1,2}$/.test(cleaned)
      ? cleaned.replace(/[.,](?=\d{1,2}$)/, '#').replace(/[.,]/g, '').replace('#', '.')
      : cleaned.replace(/[.,]/g, '');
    return Number(normalized);
  };

  function track(name, params) {
    if (typeof window.gtag !== 'function') return;
    if (window.Shopify && window.Shopify.designMode) return;
    // 补公共参数（language / market_country），事件名不变，避免 GA4 报表断档。
    // 见 docs/adr/0010-ga4-calculator-funnel.md 与 docs/bfcm-tracking-inventory.md §3.5。
    const common = (window.BfmTrack && window.BfmTrack.common()) || {};
    window.gtag('event', name, Object.assign({ module: 'calculator' }, common, params || {}));
  }

  class SavingsCalculator extends HTMLElement {
    connectedCallback() {
      // x-modal re-parents itself to <body> on open, which disconnects and reconnects
      // this element. The countdown interval is cleared on disconnect, so restart it.
      if (this.initialized) {
        this.startCampaign();
        return;
      }
      this.initialized = true;
      this.bindPathTracking();

      this.data = window.SamduoSavingsCalculatorData;
      if (!this.data) {
        console.error('[savings-calculator] data file not loaded');
        return;
      }

      // Liquid's `t` filter HTML-escapes apostrophes etc.; decode before using as text.
      const decoder = document.createElement('textarea');
      const decode = (value) => {
        decoder.innerHTML = value;
        return decoder.value;
      };
      this.strings = Object.fromEntries(
        Object.entries(JSON.parse(this.querySelector('[data-sc-strings]').textContent)).map(([key, value]) => [key, decode(value)])
      );
      this.locale = INTL_LOCALES[(this.dataset.locale || 'en').split('-')[0]] || 'en-GB';
      this.storeCurrency = this.dataset.storeCurrency || 'EUR';
      this.variantId = this.dataset.variantId;
      this.unitPrice = Number(this.dataset.unitPrice || 0) / 100;
      this.productTitle = this.dataset.productTitle || 'SAMDUO Nex E6000';
      this.campaignDiscounts = (this.dataset.campaignDiscounts || '')
        .split(',')
        .map(Number)
        .filter((value) => Number.isFinite(value) && value > 0);
      this.campaignCode = this.dataset.campaignCode || '';
      this.campaignActive = false;

      const country = this.data.countries[this.dataset.country] ? this.dataset.country : 'NL';
      const countryData = this.data.countries[country];

      this.state = {
        step: 1,
        country,
        region: countryData.regions[0].name,
        postcode: '',
        postcodeStatus: 'idle',
        capacityW: 5000,
        panels: 10,
        loadMode: 'known',
        household: 0,
        load: 2500,
        importPrice: countryData.importPrice,
        exportPrice: countryData.exportPrice,
        priority: 'payback',
        results: null
      };

      this.cacheElements();
      this.bindEvents();
      this.applyCountry();
      this.renderInputs();
      this.startCampaign();
    }

    disconnectedCallback() {
      this.clearLoadingTimer();
      this.clearCampaignTimer();
    }

    /* ─────────────────────────── setup ─────────────────────────── */

    cacheElements() {
      const q = (selector) => this.querySelector(selector);
      const qa = (selector) => Array.from(this.querySelectorAll(selector));

      this.el = {
        panels: qa('[data-sc-panel]'),
        inputs: Object.fromEntries(qa('[data-sc-input]').map((node) => [node.dataset.scInput, node])),
        outputs: qa('[data-sc-out]').reduce((acc, node) => {
          (acc[node.dataset.scOut] ||= []).push(node);
          return acc;
        }, {}),
        flags: qa('[data-sc-flag]'),
        loadModeButtons: qa('[data-sc-load-mode]'),
        priorityButtons: qa('[data-sc-priority]'),
        estimateOnly: qa('[data-sc-show="estimate"]'),
        peopleIcons: qa('[data-sc-people]'),
        householdRoot: q('[data-sc-household]'),
        householdMenu: q('[data-sc-household] .sc-select__menu'),
        advanced: q('[data-sc-advanced]'),
        countryRoot: q('[data-sc-country]'),
        countryMenu: q('[data-sc-country] .sc-select__menu'),
        countryOptions: qa('[data-sc-country-option]'),
        countryLabel: q('[data-sc-country-label]'),
        householdOptions: qa('[data-sc-household-option]'),
        householdLabel: q('[data-sc-household-label]'),
        knownOnly: qa('[data-sc-show="known"]'),
        regionField: q('[data-sc-region-field]'),
        postcodeStatus: q('[data-sc-postcode-status]'),
        panelsHint: q('[data-sc-panels-hint]'),
        info: q('[data-sc-info]'),
        limited: q('[data-sc-limited]'),
        buyNow: q('[data-sc-action="buy-now"]'),
        save: q('[data-sc-save]'),
        saveStatus: q('[data-sc-save-status]'),
        body: q('.sc__body')
      };
    }

    bindPathTracking() {
      track('calculator_page_view');
      const modal = this.closest('x-modal');
      if (!modal || modal.dataset.scPathBound === 'true') return;
      modal.dataset.scPathBound = 'true';
      modal.addEventListener('toggle', () => {
        if (modal.hasAttribute('open')) track('calculator_open');
      });
    }

    bindEvents() {
      const { inputs } = this.el;

      inputs.country.addEventListener('click', () => {
        const open = this.el.countryMenu.hidden;
        this.setHouseholdMenu(false);
        this.setCountryMenu(open);
        if (open) this.el.countryOptions.find((option) => option.dataset.scCountryOption === this.state.country)?.focus();
      });
      inputs.country.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.setHouseholdMenu(false);
          this.setCountryMenu(true);
          this.el.countryOptions.find((option) => option.dataset.scCountryOption === this.state.country)?.focus();
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.setCountryMenu(this.el.countryMenu.hidden);
        }
      });
      this.el.countryOptions.forEach((option) => {
        option.addEventListener('click', () => {
          this.selectCountry(option.dataset.scCountryOption);
          this.setCountryMenu(false);
          inputs.country.focus();
        });
      });
      this.el.countryMenu.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.stepCountry(event.key === 'ArrowDown' ? 1 : -1);
          return;
        }
        if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ' || event.key === 'Tab') {
          if (event.key !== 'Tab') event.preventDefault();
          this.setCountryMenu(false);
          inputs.country.focus();
        }
      });

      inputs.postcode.addEventListener('input', (event) => {
        this.state.postcode = event.target.value;
        this.matchPostcode();
        this.renderLocation();
      });

      inputs.region.addEventListener('change', (event) => {
        this.state.region = event.target.value;
        this.renderLocation();
      });

      this.querySelectorAll('[data-sc-panels-step]').forEach((button) => {
        button.addEventListener('click', () => this.setPanels(this.state.panels + Number(button.dataset.scPanelsStep)));
      });
      inputs.panels.addEventListener('change', (event) => this.setPanels(parseNumber(event.target.value)));

      inputs.capacity.addEventListener('input', (event) => this.setCapacity(Number(event.target.value)));
      inputs.capacityText.addEventListener('change', (event) => this.setCapacity(parseNumber(event.target.value)));

      this.el.loadModeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.state.loadMode = button.dataset.scLoadMode;
          if (this.state.loadMode === 'estimate') {
            this.state.load = this.householdLoad();
          }
          this.renderLoad();
        });
      });

      inputs.household.addEventListener('click', () => {
        const open = this.el.householdMenu.hidden;
        this.setCountryMenu(false);
        this.setHouseholdMenu(open);
        if (open) this.el.householdOptions[this.state.household]?.focus();
      });
      inputs.household.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.setHouseholdMenu(true);
          this.el.householdOptions[this.state.household]?.focus();
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.setHouseholdMenu(this.el.householdMenu.hidden);
        }
      });
      this.el.householdOptions.forEach((option) => {
        option.addEventListener('click', () => {
          this.state.household = Number(option.dataset.scHouseholdOption);
          this.state.load = this.householdLoad();
          this.setHouseholdMenu(false);
          this.renderLoad();
          inputs.household.focus();
        });
      });
      this.el.householdMenu.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.stepHousehold(event.key === 'ArrowDown' ? 1 : -1);
          return;
        }
        if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ' || event.key === 'Tab') {
          if (event.key !== 'Tab') event.preventDefault();
          this.setHouseholdMenu(false);
          inputs.household.focus();
        }
      });
      document.addEventListener('pointerdown', (event) => {
        if (!this.el.householdRoot.contains(event.target)) this.setHouseholdMenu(false);
        if (!this.el.countryRoot.contains(event.target)) this.setCountryMenu(false);
      });

      inputs.load.addEventListener('input', (event) => this.setLoad(Number(event.target.value)));
      inputs.loadText.addEventListener('change', (event) => this.setLoad(parseNumber(event.target.value)));

      inputs.importPrice.addEventListener('change', (event) => {
        const value = parseNumber(event.target.value);
        this.state.importPrice = Number.isFinite(value) && value >= 0 ? value : this.countryData().importPrice;
        this.renderPrices();
      });
      inputs.exportPrice.addEventListener('change', (event) => {
        const value = parseNumber(event.target.value);
        this.state.exportPrice = Number.isFinite(value) && value >= 0 ? value : this.countryData().exportPrice;
        this.renderPrices();
      });
      this.el.advanced?.addEventListener('toggle', () => {
        this.renderLocation();
      });

      this.el.priorityButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.state.priority = button.dataset.scPriority;
          this.renderResults();
        });
      });

      this.querySelector('[data-sc-action="calculate"]').addEventListener('click', () => this.calculate());
      this.querySelector('[data-sc-action="restart"]').addEventListener('click', () => {
        this.clearLoadingTimer();
        this.goToStep(1);
      });
      this.el.buyNow.addEventListener('click', () => this.buyNow());

      const { save } = this.el;
      if (save) {
        save.addEventListener('submit', (event) => {
          event.preventDefault();
          this.saveResults();
        });
        save.addEventListener('input', (event) => {
          // A new address is a new request, so the button offers to save again.
          if (event.target.name === 'email' && this.isSaved()) {
            this.setSaved(false);
            this.setSaveStatus(null);
          }
          if (event.target.getAttribute('aria-invalid') !== 'true') return;
          event.target.removeAttribute('aria-invalid');
          this.setSaveStatus(null);
        });
      }
    }

    /* ─────────────────────────── state helpers ─────────────────────────── */

    countryData() {
      return this.data.countries[this.state.country];
    }

    countryName() {
      return this.el.countryLabel?.textContent.trim() || this.state.country;
    }

    selectCountry(code) {
      if (!this.data.countries[code] || code === this.state.country) {
        this.syncCountryControl();
        return;
      }
      this.state.country = code;
      this.applyCountry();
      this.renderInputs();
    }

    setCountryMenu(open) {
      const { countryMenu, countryRoot, inputs } = this.el;
      if (!countryMenu || !countryRoot) return;
      countryMenu.hidden = !open;
      countryRoot.classList.toggle('sc-select--open', open);
      inputs.country.setAttribute('aria-expanded', String(open));
    }

    stepCountry(delta) {
      const options = this.el.countryOptions;
      const index = options.findIndex((option) => option.dataset.scCountryOption === this.state.country);
      const next = options[(index + delta + options.length) % options.length];
      this.selectCountry(next.dataset.scCountryOption);
      next.focus();
    }

    syncCountryControl() {
      this.el.flags.forEach((flag) => {
        flag.toggleAttribute('hidden', flag.dataset.scFlag !== this.state.country);
      });
      this.el.countryOptions.forEach((option) => {
        const selected = option.dataset.scCountryOption === this.state.country;
        option.setAttribute('aria-selected', String(selected));
        if (selected) this.el.countryLabel.textContent = option.querySelector('.sc-select__option-label').textContent.trim();
      });
    }

    /*
     * Until a postcode resolves to a region (or the user picks one manually)
     * the calculator runs on the country-level default: the national average
     * yield, labelled with the country name as in the design.
     */
    regionData() {
      const { regions } = this.countryData();
      const region = regions.find((item) => item.name === this.state.region);
      if (region) return region;
      const average = regions.reduce((sum, item) => sum + item.pvYield, 0) / regions.length;
      return { name: this.countryName(), pvYield: roundTo(average, 10), isCountryDefault: true };
    }

    householdLoad() {
      return this.countryData().householdLoads[this.state.household];
    }

    /*
     * Advanced Price Settings is NL-only. Custom import / export rates only
     * apply while that panel is open. Closing it (or switching country) keeps
     * typed values in the fields but calculation falls back to country defaults.
     */
    advancedPricesAvailable() {
      return this.state.country === 'NL';
    }

    advancedPricesOn() {
      return this.advancedPricesAvailable() && Boolean(this.el.advanced?.open);
    }

    syncAdvancedAvailability() {
      const panel = this.el.advanced;
      if (!panel) return;
      const allowed = this.advancedPricesAvailable();
      panel.hidden = !allowed;
      if (!allowed) panel.open = false;
    }

    readPriceInput(input, fallback) {
      const value = parseNumber(input?.value);
      return Number.isFinite(value) && value >= 0 ? value : fallback;
    }

    activePrices() {
      const country = this.countryData();
      if (!this.advancedPricesOn()) {
        return { importPrice: country.importPrice, exportPrice: country.exportPrice };
      }
      const importPrice = this.readPriceInput(this.el.inputs.importPrice, country.importPrice);
      const exportPrice = this.readPriceInput(this.el.inputs.exportPrice, country.exportPrice);
      this.state.importPrice = importPrice;
      this.state.exportPrice = exportPrice;
      return { importPrice, exportPrice };
    }

    setHouseholdMenu(open) {
      const { householdMenu, householdRoot, inputs } = this.el;
      if (!householdMenu || !householdRoot) return;
      householdMenu.hidden = !open;
      householdRoot.classList.toggle('sc-select--open', open);
      inputs.household.setAttribute('aria-expanded', String(open));
    }

    stepHousehold(delta) {
      const count = this.el.householdOptions.length;
      this.state.household = (this.state.household + delta + count) % count;
      this.state.load = this.householdLoad();
      this.renderLoad();
      this.el.householdOptions[this.state.household]?.focus();
    }

    get kwp() {
      return this.state.capacityW / 1000;
    }

    /*
     * Panel count and total capacity are two views of the same value:
     * capacity = panels × panelWatt. The slider therefore steps by one
     * panel so that both fields always stay in exact sync.
     */
    setPanels(value) {
      if (!Number.isFinite(value)) {
        this.renderSolar();
        return;
      }
      const outOfRange = value < LIMITS.panels.min || value > LIMITS.panels.max;
      this.state.panels = clamp(Math.round(value), LIMITS.panels.min, LIMITS.panels.max);
      this.state.capacityW = this.state.panels * this.data.product.panelWatt;
      this.el.panelsHint.textContent = this.strings.panelsOutOfRange;
      this.el.panelsHint.hidden = !outOfRange;
      this.renderSolar();
    }

    setCapacity(value) {
      if (!Number.isFinite(value)) {
        this.renderSolar();
        return;
      }
      this.state.panels = clamp(Math.round(value / this.data.product.panelWatt), LIMITS.panels.min, LIMITS.panels.max);
      this.state.capacityW = this.state.panels * this.data.product.panelWatt;
      this.el.panelsHint.hidden = true;
      this.renderSolar();
    }

    setLoad(value) {
      if (!Number.isFinite(value)) {
        this.renderLoad();
        return;
      }
      this.state.load = clamp(roundTo(value, LIMITS.load.step), LIMITS.load.min, LIMITS.load.max);
      this.renderLoad();
    }

    applyCountry() {
      const country = this.countryData();
      this.state.region = '';
      this.state.postcode = '';
      this.state.postcodeStatus = 'idle';
      this.state.importPrice = country.importPrice;
      this.state.exportPrice = country.exportPrice;
      if (this.state.loadMode === 'estimate') {
        this.state.load = this.householdLoad();
      }

      const { inputs } = this.el;
      inputs.postcode.value = '';
      inputs.postcode.placeholder = this.fill(this.strings.postcodePlaceholder, { example: country.postcodeExample });
      inputs.region.innerHTML = country.regions
        .map((region) => `<option value="${region.name}">${region.name}</option>`)
        .join('');
      this.syncCountryControl();
      this.syncAdvancedAvailability();
    }

    matchPostcode() {
      const { country, postcode } = this.state;
      const match = this.data.postcodeToRegion(country, postcode);
      const { regions } = this.countryData();
      if (match && regions.some((region) => region.name === match)) {
        this.state.region = match;
        this.state.postcodeStatus = 'matched';
      } else if (this.data.isPostcodeComplete(country, postcode)) {
        // Fall back to a manual region picker, preselecting the first region.
        if (!regions.some((region) => region.name === this.state.region)) {
          this.state.region = regions[0].name;
        }
        this.state.postcodeStatus = 'nomatch';
      } else {
        this.state.region = '';
        this.state.postcodeStatus = 'idle';
      }
    }

    /* ─────────────────────────── calculation ─────────────────────────── */

    compute() {
      const { product, ratioBands, directUseFactor, batteryUtilization } = this.data;
      const { rte, usableRatio, nominalCapacityKwh } = {
        rte: product.roundTripEfficiency,
        usableRatio: product.usableRatio,
        nominalCapacityKwh: product.nominalCapacityKwh
      };

      const pv = this.kwp * this.regionData().pvYield;
      const load = this.state.load;
      const ratio = load > 0 ? pv / load : Infinity;
      const band = Math.max(ratioBands.findIndex((max) => ratio < max), 0);
      const directFactor = directUseFactor[band];
      const { importPrice, exportPrice } = this.activePrices();

      const directPv = Math.min(pv, load) * directFactor;
      const surplus = Math.max(pv - directPv, 0);
      const deficit = Math.max(load - directPv, 0);

      const options = [];
      for (let units = 1; units <= product.maxUnits; units += 1) {
        const usable = units * nominalCapacityKwh * usableRatio;
        const utilization = batteryUtilization[band][units - 1];
        const input = Math.min(surplus, deficit / rte, usable * utilization);
        const output = input * rte;
        const saving = Math.max(output * importPrice - input * exportPrice, 0);
        const listPrice = this.unitPrice * units;
        const price = Math.max(listPrice - this.campaignDiscount(units), 0);
        options.push({
          units,
          input,
          output,
          saving,
          listPrice,
          price,
          payback: saving > 0 && price > 0 ? price / saving : Infinity,
          selfUseAfter: pv > 0 ? clamp((directPv + input) / pv, 0, 1) : null,
          independence: load > 0 ? clamp((directPv + output) / load, 0, 1) : null
        });
      }

      return { pv, load, ratio, band, directFactor, directPv, surplus, deficit, options };
    }

    campaignDiscount(units) {
      const tiers = this.campaignDiscounts;
      if (!this.campaignActive || !tiers.length) return 0;
      return tiers[Math.min(units, tiers.length) - 1];
    }

    recommend(options) {
      const limit = this.data.product.incrementalPaybackLimitYears;

      if (this.state.priority === 'saving') {
        const best = options.reduce((acc, option) => (option.saving > acc.saving ? option : acc), options[0]);
        return { units: best.units, limited: best.saving <= 0 };
      }

      if (options[0].saving <= 0) {
        return { units: 1, limited: true };
      }
      let units = 1;
      for (let index = 1; index < options.length; index += 1) {
        const current = options[index];
        const previous = options[index - 1];
        const incrementalSaving = current.saving - previous.saving;
        const incrementalPrice = current.price - previous.price;
        if (incrementalSaving <= 0) break;
        if (incrementalPrice > 0 && incrementalPrice / incrementalSaving > limit) break;
        units = current.units;
      }
      return { units, limited: false };
    }

    calculate() {
      if (this.state.step === 'loading') return;

      track('calculator_check_results');
      this.clearLoadingTimer();
      this.setSaveStatus(null);
      const prices = this.activePrices();
      this.state.results = this.compute();
      this.goToStep('loading');
      this.loadingTimer = setTimeout(() => {
        this.loadingTimer = null;
        this.goToStep(2);
        this.renderResults();
        this.dispatchEvent(
          new CustomEvent('savings-calculator:calculated', {
            bubbles: true,
            detail: {
              modelVersion: this.data.product.modelVersion,
              factorVersion: this.data.product.factorVersion,
              inputs: {
                ...this.state,
                results: undefined,
                advancedPrices: this.advancedPricesOn(),
                importPrice: prices.importPrice,
                exportPrice: prices.exportPrice
              },
              results: this.state.results
            }
          })
        );
      }, 1000);
    }

    startCampaign() {
      this.clearCampaignTimer();
      const banner = this.querySelector('[data-sc-campaign]');
      if (!banner) return;
      if (typeof window.SamduoZonedTime?.stamp !== 'function') {
        whenZonedTimeReady(this.dataset.zonedTimeSrc)
          .then(() => {
            if (this.isConnected) this.startCampaign();
          })
          .catch((error) => console.error(error));
        return;
      }
      const timeZone = banner.dataset.scCampaignTimezone || 'Europe/Amsterdam';
      const start = window.SamduoZonedTime.stamp(banner.dataset.scCampaignStart, timeZone);
      const end = window.SamduoZonedTime.stamp(banner.dataset.scCampaignEnd, timeZone);
      const pad = (value) => String(value).padStart(2, '0');
      const nodes = {
        days: banner.querySelector('[data-sc-countdown="days"]'),
        hours: banner.querySelector('[data-sc-countdown="hours"]'),
        minutes: banner.querySelector('[data-sc-countdown="minutes"]'),
        seconds: banner.querySelector('[data-sc-countdown="seconds"]')
      };
      const tick = () => {
        const now = Date.now();
        this.setCampaignActive(now >= start && now < end);
        if (!this.campaignActive) {
          banner.hidden = true;
          this.clearCampaignTimer();
          return;
        }
        let remain = end - now;
        const days = Math.floor(remain / 86400000);
        remain -= days * 86400000;
        const hours = Math.floor(remain / 3600000);
        remain -= hours * 3600000;
        const minutes = Math.floor(remain / 60000);
        const seconds = Math.floor((remain - minutes * 60000) / 1000);
        nodes.days.textContent = pad(days);
        nodes.hours.textContent = pad(hours);
        nodes.minutes.textContent = pad(minutes);
        nodes.seconds.textContent = pad(seconds);
      };
      tick();
      this.campaignTimer = window.setInterval(tick, 1000);
    }

    /* Campaign pricing follows the banner window, so re-price shown results when it flips. */
    setCampaignActive(active) {
      if (active === this.campaignActive) return;
      this.campaignActive = active;
      if (!this.state.results) return;
      this.state.results = this.compute();
      this.renderResults();
    }

    clearCampaignTimer() {
      if (!this.campaignTimer) return;
      clearInterval(this.campaignTimer);
      this.campaignTimer = null;
    }

    clearLoadingTimer() {
      if (!this.loadingTimer) return;
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }

    /* ─────────────────────────── formatting ─────────────────────────── */

    fill(template, values) {
      return Object.entries(values).reduce(
        (text, [key, value]) => text.replaceAll(`__${key.toUpperCase()}__`, String(value)),
        template || ''
      );
    }

    formatInt(value) {
      return new Intl.NumberFormat(this.locale, { maximumFractionDigits: 0 }).format(value);
    }

    formatWithUnit(value, unit) {
      const text = `${this.formatInt(value)}${unit || ''}`;
      return text;
    }

    fillValue(input, value, unit) {
      input.value = this.formatWithUnit(value, unit);
      input.size = Math.max(input.value.length, 4);
    }

    formatMoney(value, currency, fractionDigits = 0) {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      }).format(value);
    }

    formatPercent(value) {
      return new Intl.NumberFormat(this.locale, { style: 'percent', maximumFractionDigits: 0 }).format(value);
    }

    currencySymbol(currency) {
      const parts = new Intl.NumberFormat(this.locale, { style: 'currency', currency }).formatToParts(0);
      return parts.find((part) => part.type === 'currency')?.value || currency;
    }

    setOutput(name, value) {
      (this.el.outputs[name] || []).forEach((node) => {
        node.textContent = value;
      });
    }

    /* ─────────────────────────── rendering ─────────────────────────── */

    renderInputs() {
      this.renderSolar();
      this.renderLoad();
      this.renderLocation();
      this.renderPrices();
    }

    renderSolar() {
      const { inputs } = this.el;
      const watt = this.data.product.panelWatt;
      inputs.capacity.min = LIMITS.panels.min * watt;
      inputs.capacity.max = LIMITS.panels.max * watt;
      inputs.capacity.step = watt;
      inputs.panels.value = this.state.panels;
      inputs.capacity.value = this.state.capacityW;
      this.fillValue(inputs.capacityText, this.state.capacityW, this.strings.unitWatt);
      this.paintSlider(inputs.capacity);
    }

    renderLoad() {
      const { inputs } = this.el;
      const estimate = this.state.loadMode === 'estimate';
      this.el.loadModeButtons.forEach((button) => {
        button.setAttribute('aria-checked', String(button.dataset.scLoadMode === this.state.loadMode));
      });
      this.el.estimateOnly.forEach((node) => {
        node.hidden = !estimate;
      });
      this.el.knownOnly.forEach((node) => {
        node.hidden = estimate;
      });
      if (!estimate) this.setHouseholdMenu(false);
      const peopleIcon = ['2', '3', '5'][this.state.household] || '2';
      this.el.peopleIcons.forEach((icon) => {
        icon.hidden = icon.dataset.scPeople !== peopleIcon;
      });
      this.el.householdOptions.forEach((option) => {
        const selected = Number(option.dataset.scHouseholdOption) === this.state.household;
        option.setAttribute('aria-selected', String(selected));
        if (selected) this.el.householdLabel.textContent = option.querySelector('.sc-select__option-label').textContent;
      });
      inputs.load.value = this.state.load;
      this.fillValue(inputs.loadText, this.state.load, this.strings.unitKwhYear);
      this.setOutput(
        'consumptionEstimated',
        this.fill(this.strings.consumptionEstimated, { value: this.formatInt(this.state.load) })
      );
      this.paintSlider(inputs.load);
    }

    renderLocation() {
      const country = this.countryData();
      const region = this.regionData();
      const { postcodeStatus } = this.state;

      if (!region.isCountryDefault) this.el.inputs.region.value = region.name;
      this.el.regionField.hidden = postcodeStatus !== 'nomatch';

      this.el.postcodeStatus.hidden = postcodeStatus === 'idle';
      this.el.postcodeStatus.classList.toggle('sc-field__hint--warning', postcodeStatus === 'nomatch');
      this.el.postcodeStatus.classList.toggle('sc-field__hint--success', postcodeStatus === 'matched');
      this.el.postcodeStatus.textContent =
        postcodeStatus === 'matched'
          ? this.fill(this.strings.matched, { region: region.name })
          : postcodeStatus === 'nomatch'
            ? this.strings.noMatch
            : '';

      this.setOutput('regionName', region.name);
      this.setOutput(
        'yieldInfo',
        this.fill(this.strings.yieldInfo, {
          yield: this.formatInt(region.pvYield),
          price: this.formatMoney(country.importPrice, country.currency, 2)
        })
      );
    }

    renderPrices() {
      const { inputs } = this.el;
      const country = this.countryData();
      inputs.importPrice.value = this.state.importPrice.toFixed(2);
      inputs.exportPrice.value = this.state.exportPrice.toFixed(2);
      this.setOutput('perKwh', this.fill(this.strings.perKwh, { currency: this.currencySymbol(country.currency) }));
      this.renderLocation();
    }

    paintSlider(input) {
      const min = Number(input.min);
      const max = Number(input.max);
      const ratio = clamp((Number(input.value) - min) / (max - min), 0, 1);
      input.style.setProperty('--sc-slider-fill', `${(ratio * 100).toFixed(2)}%`);
    }

    renderResults() {
      const results = this.state.results;
      if (!results) return;

      // The saved email described the previous result, so changed results can be saved again.
      if (this.isSaved()) {
        this.setSaved(false);
        this.setSaveStatus(null);
      }

      const { options } = results;
      const { units, limited } = this.recommend(options);
      const selected = options[units - 1];
      const { currency, gridEmissionFactor } = this.countryData();

      this.el.priorityButtons.forEach((button) => {
        button.setAttribute('aria-checked', String(button.dataset.scPriority === this.state.priority));
      });
      const highlightSaving = this.state.priority === 'saving';
      (this.el.outputs.payback || []).forEach((node) => {
        node.classList.toggle('sc-metric__value--accent', !highlightSaving);
      });
      (this.el.outputs.saving || []).forEach((node) => {
        node.classList.toggle('sc-metric__value--accent', highlightSaving);
      });
      this.el.limited.hidden = !limited;

      const hasPayback = Number.isFinite(selected.payback);
      // Grid electricity displaced by the battery × national grid CO₂ intensity.
      const co2 = selected.output * gridEmissionFactor;
      const display = {
        payback: hasPayback ? this.fill(this.strings.years, { value: selected.payback.toFixed(1) }) : this.strings.noPayback,
        saving: this.formatMoney(selected.saving, currency),
        gridSaved: this.fill(this.strings.kwhPerYear, { value: this.formatInt(selected.output) }),
        selfUse: selected.selfUseAfter === null ? this.strings.notApplicable : this.formatPercent(selected.selfUseAfter),
        independence: selected.independence === null ? this.strings.notApplicable : this.formatPercent(selected.independence),
        co2: this.fill(this.strings.kgPerYear, { value: this.formatInt(co2) }),
        recommendedTitle: this.fill(this.strings.recommendedTitle, { count: units }),
        price: this.formatMoney(selected.listPrice, this.storeCurrency, 2)
      };
      Object.entries(display).forEach(([name, value]) => this.setOutput(name, value));
      this.el.buyNow.dataset.quantity = String(units);

      const round = (value, digits) => (value === null ? null : Number(value.toFixed(digits)));
      this.summary = {
        priority: this.state.priority,
        limited_savings: limited,
        recommended_units: units,
        recommended_title: display.recommendedTitle,
        payback_years: hasPayback ? round(selected.payback, 1) : null,
        payback_display: display.payback,
        annual_saving: round(selected.saving, 2),
        annual_saving_display: display.saving,
        self_consumption: round(selected.selfUseAfter, 2),
        self_consumption_display: display.selfUse,
        grid_saved_kwh: Math.round(selected.output),
        grid_saved_display: display.gridSaved,
        energy_independence: round(selected.independence, 2),
        energy_independence_display: display.independence,
        co2_reduction_kg: Math.round(co2),
        co2_reduction_display: display.co2,
        price: round(selected.listPrice, 2),
        price_display: display.price,
        currency: this.storeCurrency
      };
    }

    goToStep(step) {
      this.state.step = step;
      this.dataset.step = String(step);
      this.toggleAttribute('aria-busy', step === 'loading');
      this.el.panels.forEach((panel) => {
        panel.hidden = panel.dataset.scPanel !== String(step);
      });
      // Mobile/tablet scroll the host itself, desktop scrolls the form column.
      this.scrollTo({ top: 0, behavior: 'auto' });
      if (this.el.body) this.el.body.scrollTo({ top: 0, behavior: 'auto' });
    }

    /* ─────────────────────────── save results ─────────────────────────── */

    setSaveStatus(type, message = '') {
      const status = this.el.saveStatus;
      if (!status) return;
      status.hidden = !type;
      status.textContent = type ? message : '';
      status.classList.toggle('sc-save__status--error', type === 'error');
      status.classList.toggle('sc-save__status--success', type === 'success');
    }

    saveButton() {
      return this.el.save?.querySelector('[type="submit"]') || null;
    }

    isSaved() {
      return Boolean(this.saveButton()?.hasAttribute('data-saved'));
    }

    setSaved(saved) {
      const button = this.saveButton();
      if (!button) return;
      button.toggleAttribute('data-saved', saved);
      button.disabled = saved;
    }

    resultsEventProperties() {
      const { results, loadMode, household, panels, capacityW, load, country } = this.state;
      const prices = this.activePrices();
      return {
        ...this.summary,
        country,
        region: this.regionData().name,
        postcode: this.state.postcode.trim(),
        panels,
        pv_capacity_w: capacityW,
        pv_yield_kwh: Math.round(results.pv),
        load_mode: loadMode,
        household_size: loadMode === 'estimate' ? this.el.householdLabel?.textContent.trim() || '' : '',
        annual_consumption_kwh: load,
        import_price: prices.importPrice,
        export_price: prices.exportPrice,
        product_title: this.productTitle,
        product_specs: this.dataset.productSpecs || '',
        product_url: absoluteUrl(this.dataset.productUrl),
        product_image_url: absoluteUrl(this.dataset.productImage),
        locale: this.dataset.locale || '',
        email_subject: this.strings.emailSubject || '',
        page_url: window.location.href,
        model_version: this.data.product.modelVersion
      };
    }

    async saveResults() {
      const form = this.el.save;
      if (!form || this.saving || !this.summary) return;

      const { email: emailInput, terms, marketing } = form.elements;
      const email = emailInput.value.trim();
      if (!EMAIL_PATTERN.test(email)) {
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
        this.setSaveStatus('error', this.strings.emailInvalid);
        return;
      }
      if (!terms.checked) {
        terms.setAttribute('aria-invalid', 'true');
        terms.focus();
        this.setSaveStatus('error', this.strings.termsRequired);
        return;
      }

      const listId = this.dataset.klaviyoListId || '';
      const optIn = marketing.checked;
      const profile = { data: { type: 'profile', attributes: { email } } };
      const requests = [
        postToKlaviyo('events', {
          data: {
            type: 'event',
            attributes: {
              properties: this.resultsEventProperties(),
              metric: { data: { type: 'metric', attributes: { name: KLAVIYO.metric } } },
              profile,
              unique_id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
            }
          }
        })
      ];
      if (optIn && listId) {
        requests.push(
          postToKlaviyo('subscriptions', {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.dataset.klaviyoSource || 'Savings Calculator',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } }
                  }
                }
              },
              relationships: { list: { data: { type: 'list', id: listId } } }
            }
          })
        );
      }

      const button = this.saveButton();
      this.saving = true;
      button.setAttribute('aria-busy', 'true');
      this.setSaveStatus(null);

      const [event, subscription] = await Promise.allSettled(requests);
      this.saving = false;
      button.removeAttribute('aria-busy');

      // The results email depends only on the event; a failed list signup is logged, not shown.
      if (subscription?.status === 'rejected') {
        console.error('[savings-calculator] Klaviyo subscription failed', subscription.reason);
      }
      if (event.status === 'rejected') {
        console.error('[savings-calculator] Klaviyo event failed', event.reason);
        this.setSaveStatus('error', this.strings.saveError);
        return;
      }

      track('calculator_save_results', { marketing_opt_in: optIn });
      this.setSaved(true);
      this.setSaveStatus('success', this.fill(this.strings.saveSuccess, { email }));
    }

    /* ─────────────────────────── checkout ─────────────────────────── */

    /**
     * Same flow as the theme's own "Buy now" (`data-buy-now-permalink` in
     * buy-buttons.liquid): a cart permalink with the recommended quantity sends
     * the visitor straight to checkout. The button is a theme `hover-button`, so
     * `aria-busy` shows the standard dot loader and `pageshow` clears it on back.
     */
    buyNow() {
      const button = this.el.buyNow;
      if (button.hasAttribute('aria-busy') || button.disabled) return;

      const quantity = Math.max(Number(button.dataset.quantity) || 1, 1);
      track('calculator_buy_now');
      const rootUrl = (theme.routes.root_url || '/').replace(/\/$/, '');

      const discount = this.campaignActive && this.campaignCode ? `?discount=${encodeURIComponent(this.campaignCode)}` : '';

      button.setAttribute('aria-busy', 'true');
      window.location.href = `${rootUrl}/cart/${this.variantId}:${quantity}${discount}`;
    }
  }

  customElements.define('savings-calculator', SavingsCalculator);
})();
